import { CandidateRoleScreeningStatus } from '@internal/api/roles/roles';

import {
  hasVerificationOutcome,
  isCallCompleted,
  isInterviewCleared,
  isInterviewCompleted,
  isReviewPassed,
  isReviewWaitlisted,
  isRoleVerified,
  isScreeningCompleted,
  isTestCompleted,
  isVerificationFailed,
  RoleProgressSource,
} from './role-types';

export enum RoleStepKey {
  Test = 'test',
  Call = 'call',
  Review = 'review',
  Recruiter = 'recruiter',
  Technical = 'technical',
  Verification = 'verification',
}

const ROLE_STEP_KEYS: readonly RoleStepKey[] = [
  RoleStepKey.Test,
  RoleStepKey.Call,
  RoleStepKey.Review,
  RoleStepKey.Recruiter,
  RoleStepKey.Technical,
  RoleStepKey.Verification,
];

export const ROLE_STEP_COUNT = ROLE_STEP_KEYS.length;

export type RoleStepsDone = Record<RoleStepKey, boolean>;

export const getRoleStepsDone = (role: RoleProgressSource): RoleStepsDone => {
  const verification = hasVerificationOutcome(role);
  const technical = verification || isInterviewCompleted(role);
  const screening = role.assessment.screening.status;
  const recruiter =
    technical ||
    screening === CandidateRoleScreeningStatus.Completed ||
    screening === CandidateRoleScreeningStatus.Skipped ||
    isInterviewCleared(role);
  const review = recruiter || isReviewPassed(role) || isReviewWaitlisted(role);

  return {
    [RoleStepKey.Test]: verification || isTestCompleted(role),
    [RoleStepKey.Call]: verification || isCallCompleted(role),
    [RoleStepKey.Review]: review,
    [RoleStepKey.Recruiter]: recruiter,
    [RoleStepKey.Technical]: technical,
    [RoleStepKey.Verification]: verification,
  };
};

export const countRoleStepsDone = (role: RoleProgressSource): number =>
  Object.values(getRoleStepsDone(role)).filter(Boolean).length;

export interface RoleOutcome {
  stepKey: RoleStepKey;
  message: string;
  ctaLabel: string;
}

export const ROLE_CAPACITY_MESSAGE =
  "You're on the waitlist — we're at capacity for profiles like yours in this role. You can start another role track in the meantime.";

export const ROLE_REJECTION_MESSAGE =
  'No luck this time. We will let you know when you can apply for this role again. In the meantime, feel free to explore other roles.';

export const getRoleOutcome = (
  role: RoleProgressSource
): RoleOutcome | null => {
  if (isRoleVerified(role)) return null;

  if (isVerificationFailed(role)) {
    return {
      stepKey: RoleStepKey.Verification,
      message: ROLE_REJECTION_MESSAGE,
      ctaLabel: 'Explore more roles',
    };
  }

  if (!isReviewWaitlisted(role)) return null;

  if (isInterviewCompleted(role)) {
    return {
      stepKey: RoleStepKey.Technical,
      message: ROLE_REJECTION_MESSAGE,
      ctaLabel: 'Explore other roles',
    };
  }

  if (isScreeningCompleted(role)) {
    return {
      stepKey: RoleStepKey.Recruiter,
      message: ROLE_REJECTION_MESSAGE,
      ctaLabel: 'See other roles',
    };
  }

  return {
    stepKey: RoleStepKey.Review,
    message: ROLE_CAPACITY_MESSAGE,
    ctaLabel: 'Explore other roles',
  };
};

enum RoleListRank {
  InProgress = 0,
  Verified = 1,
  Closed = 2,
}

export const getRoleListRank = (role: RoleProgressSource): RoleListRank => {
  if (isRoleVerified(role)) return RoleListRank.Verified;
  return getRoleOutcome(role) ? RoleListRank.Closed : RoleListRank.InProgress;
};
